import { createTheme, responsiveFontSizes } from "@mui/material/styles";

const PALETTES = {
  navy: {
    background:  { default: "#0f172a", paper: "#1e293b" },
    text:        { primary: "#f1f5f9", secondary: "#94a3b8" },
    divider:     "#334155",
    tableHead:   "#0f172a",
    inputBorder: "#334155",
    inputHover:  "#475569",
    acBg:        "#253347",
    acBorder:    "#334155",
  },
  black: {
    background:  { default: "#000000", paper: "#0e0e0e" },
    text:        { primary: "#f1f5f9", secondary: "#8a8f98" },
    divider:     "#1e1e1e",
    tableHead:   "#000000",
    inputBorder: "#222222",
    inputHover:  "#333333",
    acBg:        "#111111",
    acBorder:    "#252525",
  },
};

export const createAppTheme = (variant = "navy") => {
  const p = PALETTES[variant] || PALETTES.navy;

  let t = createTheme({
    breakpoints: {
      values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536, xxl: 2560 },
    },
    palette: {
      mode: "dark",
      primary:    { main: "#3b82f6", light: "#60a5fa", dark: "#1d4ed8" },
      secondary:  { main: "#10b981", light: "#34d399", dark: "#059669" },
      error:      { main: "#ef4444" },
      warning:    { main: "#f59e0b" },
      success:    { main: "#10b981" },
      background: p.background,
      text:       p.text,
      divider:    p.divider,
    },
    typography: {
      fontFamily: '"Inter", "Roboto", sans-serif',
      h4: { fontWeight: 700, "@media (min-width: 2560px)": { fontSize: "2.6rem" } },
      h5: { fontWeight: 700, "@media (min-width: 2560px)": { fontSize: "2rem" } },
      h6: { fontWeight: 600, "@media (min-width: 2560px)": { fontSize: "1.4rem" } },
      body2: { "@media (min-width: 2560px)": { fontSize: "0.95rem" } },
      caption: { "@media (min-width: 2560px)": { fontSize: "0.78rem" } },
      subtitle1: { fontWeight: 500 },
      button: { textTransform: "none", fontWeight: 500 },
    },
    shape: { borderRadius: 10 },
    components: {
      MuiTableContainer: { styleOverrides: { root: { overflowX: "auto" } } },
      MuiDrawer:  { styleOverrides: { paper: { backgroundImage: "none" } } },
      MuiPaper:   { styleOverrides: { root:  { backgroundImage: "none" } } },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8, textTransform: "none", fontWeight: 500 },
          containedPrimary: { boxShadow: "none", "&:hover": { boxShadow: "none" } },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: p.divider },
          head: {
            fontWeight: 600, color: p.text.secondary, backgroundColor: p.tableHead,
            fontSize: "0.75rem", letterSpacing: "0.05em", textTransform: "uppercase",
            "@media (min-width: 1536px)": { fontSize: "0.78rem" },
            "@media (min-width: 2560px)": { fontSize: "0.85rem" },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: { borderColor: p.inputBorder },
          root: { "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: p.inputHover } },
        },
      },
      MuiChip:    { styleOverrides: { root: { fontWeight: 500, fontSize: "0.75rem" } } },
      MuiAlert:   { styleOverrides: { root: { borderRadius: 8 } } },
      MuiDivider: { styleOverrides: { root: { borderColor: p.divider } } },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: p.acBg,
            border: `1px solid ${p.acBorder}`,
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
          },
          option: {
            fontSize: "0.85rem",
            "&:hover, &[aria-selected='true']": { backgroundColor: "rgba(59,130,246,0.15) !important" },
            "&[aria-selected='true']": { color: "#60a5fa" },
          },
          listbox: { padding: "4px 0" },
          noOptions: { color: p.text.secondary, fontSize: "0.82rem" },
        },
      },
    },
  });

  return responsiveFontSizes(t, { factor: 2 });
};

export default createAppTheme("navy");
